import { hasItems } from "@repo/utils";
import useSWR, { mutate } from "swr";

import { useCustomerIdentifierSearch } from "../customer/customerIdentifierSearch";
import { type Customer } from "../customer/types";
import * as fetcher from "../fetcher";
import { type Opportunity } from "../opportunity/types";
import { mapApiResponseToOrders } from "./mapOrders";

export interface DraftOrderCustomer {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  mobilePhone?: string;
  accountType?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  zipCode?: string;
  countryCode?: string;
  language?: string;
  preferredLocale?: string;
  partyId?: string;
  partyUId?: string;
  ikeaFamilyNumber?: string;
  isBusinessCustomer?: boolean;
  businessCardNo?: string;
  businessName?: string;
  stateCode?: string;
}

export interface DraftOrderData {
  countryCode: string;
  buCode: string;
  status: string;
  expiresOn: string;
  metaData: {
    totalPrice: {
      priceExclTax: number;
      taxDetails: any[];
      Currency: string;
      priceInclTax: number;
      type: string;
      currencyCode: string;
    };
    imageUrls: string[];
    orderCreationDate: string;
    numberOfPackages: number;
    totalItemCount: number;
  };
  cartData?: {
    customer?: DraftOrderCustomer;
  };
}

export interface DraftOrder extends DraftOrderData {
  draftOrderId: string;
}

export type DraftOrderSearchResult = Record<number, DraftOrderData>;

export const getDraftOrderData = (
  draftOrder: DraftOrderSearchResult
): DraftOrder => {
  const key = Object.keys(draftOrder)[0];
  return {
    draftOrderId: key,
    ...draftOrder[Number(key)],
  };
};

export const getAllDraftOrdersIds = (project?: Opportunity): string[] => {
  if (!project) {
    return [];
  }

  const draftOrdersIds = project.orders?.allOrders
    ?.sort(
      (a, b) =>
        new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
    )
    ?.filter((order) => order.isDraftOrder === true)
    .map((order) => order.orderId);

  return draftOrdersIds ?? [];
};

export const getAllDraftOrderData = (
  draftOrders: DraftOrderSearchResult[]
): DraftOrder[] => {
  const ordersArray = Array.isArray(draftOrders) ? draftOrders : [draftOrders];

  return ordersArray.map(getDraftOrderData);
};

export function useDraftOrder(searchTerm?: string) {
  return useSWR<DraftOrder>(
    searchTerm ? `/${searchTerm}?includeCartData=true` : null,
    (url) =>
      fetcher.saljaDraftOrder
        .get<DraftOrderSearchResult>(url)
        .then((res) => getDraftOrderData(res)),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
}

export function useDraftOrders(draftOrderIds?: string[]) {
  const fetchDraftOrder = async (id: string) => {
    try {
      const url = `/${id}?includeCartData=true`;
      const response = await fetcher.saljaDraftOrder.get<any>(url);
      return mapApiResponseToOrders(response[id], id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching draft order for ID ${id}:`, error);
      return [];
    }
  };

  const fetchDraftOrders = async (ids: string[]) => {
    const promises = ids.map((id) => fetchDraftOrder(id));
    const results = await Promise.all(promises);
    const draftOrderData = results.flat();
    return draftOrderData;
  };

  return useSWR(
    hasItems(draftOrderIds) ? draftOrderIds.map((id) => `${id}`) : [],
    fetchDraftOrders,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );
}

const fetchDraftOrdersByPartyId = async (partyId?: string) => {
  return fetcher.saljaDraftOrder
    .get<DraftOrderSearchResult[]>(`/customer-orders/?id=${partyId}`)
    .then((res) => getAllDraftOrderData(res));
};

const fetchDraftOrdersByPartyIds = async (ids: string[]) => {
  const promises = ids.map((partyId) => fetchDraftOrdersByPartyId(partyId));
  const results = await Promise.all(promises);
  return results.flat();
};

export const useDraftOrderSearchByPartyId = (partyId?: string) => {
  return useSWR<DraftOrder[]>(
    partyId ? `/customer-orders/?id=${partyId}` : null,
    () => fetchDraftOrdersByPartyId(partyId),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
};

export const useDraftOrderSearchById = (draftOrderId?: string) => {
  return useSWR<DraftOrder[]>(
    draftOrderId ? `/${draftOrderId}` : null,
    (url) =>
      fetcher.saljaDraftOrder
        .get<DraftOrderSearchResult[]>(url)
        .then((res) => getAllDraftOrderData(res)),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
};

export function useDraftOrderSearchByEmail(
  countryCode: string,
  customerType?: Customer["customerType"],
  email?: string
) {
  const { data: partyIds } = useCustomerIdentifierSearch(
    countryCode,
    customerType,
    email
  );

  return useSWR<DraftOrder[]>(
    hasItems(partyIds) ? `draft-order-${partyIds.join(",")}` : null,
    fetchDraftOrdersByPartyIds,
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
}

export const associateDraftOrders = async (
  draftOrderIds: string[],
  opportunityId: number
) => {
  const res = await fetcher.opportunity.post<null, true>(
    `/${opportunityId}/multiorders`,
    draftOrderIds.map((orderId) => ({ orderId: orderId, isDraftOrder: true })),
    undefined,
    true
  );
  mutate(`/opportunities/${opportunityId}`);
  mutate(`/opportunities/${opportunityId}/history`);
  return res;
};
